import { useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { recordDonation } from '@generated/server/donate';
import { PayPanel } from '@generated/components/PayPanel';
import { money } from '@generated/components/pricing';

const AMOUNTS = [10, 25, 50, 100, 250];
const METHODS = ['Credit Card', 'Zelle', 'Venmo', 'PayPal'] as const;
type Method = (typeof METHODS)[number];
const field = 'mt-1 block h-11 w-full border border-input bg-background px-3 text-sm';

/** Reader donations to the Star-Revue: pick an amount, then pay by Zelle, PayPal or card. */
export function Donate() {
  const [amount, setAmount] = useState<number>(25);
  const [other, setOther] = useState('');
  const [method, setMethod] = useState<Method>('Credit Card');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; amount: number; method: Method } | null>(null);

  const total = other ? Math.round(Number(other) * 100) / 100 : amount;
  const submit = async () => {
    if (!(total >= 1)) { setError('Please choose or enter an amount of at least $1.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await recordDonation({ data: { amount: total, method, name: name || undefined, email: email || undefined, note: note || undefined, anonymous } });
      if (!r?.id) throw new Error('no id');
      setDone({ id: r.id, amount: total, method });
    } catch (e) { console.error(e); setError('Something went wrong. Please try again, or call 917-652-9128.'); }
    finally { setBusy(false); }
  };

  if (done) return (
    <section className="mx-auto max-w-2xl">
      <div className="border-2 border-foreground bg-card p-6 text-center">
        <Heart className="mx-auto h-10 w-10 fill-primary text-primary" />
        <h2 className="mt-2 font-[family-name:var(--font-heading)] text-3xl font-semibold">Thank you!</h2>
        <p className="mt-2 text-sm text-muted-foreground">Your support keeps independent local news coming to your doorstep. Finish your gift of {money(done.amount)} below.</p>
      </div>
      <PayPanel method={done.method} orderId={done.id} dueNow={done.amount} total={done.amount} monthly={false} email={email} kind="Donation" who={anonymous ? "Anonymous" : name || undefined} />
      <div className="mt-6 border-t-4 border-double border-foreground pt-5 text-center">
        <p className="font-[family-name:var(--font-heading)] text-lg italic leading-relaxed">Our sincere thanks, from the publisher and all the staff who make our paper a living thing. We promise to keep doing our best to bring you interesting and important reading.</p>
        <p className="mt-2 text-sm font-semibold">George Fiala, Publisher, and everyone at the <strong>Star-Revue</strong></p>
      </div>
    </section>
  );

  return (
    <section className="mx-auto max-w-2xl">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Support local news</div>
      <h2 className="mt-1 font-[family-name:var(--font-heading)] text-4xl font-semibold leading-tight">Make a donation to the Star-Revue</h2>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">The <strong className="text-foreground">Red Hook Star-Revue</strong> and <strong className="text-foreground">The Village Star-Revue</strong> are independent, neighborhood-owned papers, free on the street every month. Your gift, big or small, helps pay our reporters, photographers and printer, and keeps local voices in print.</p>

      <div className="mt-6 border-2 border-foreground bg-card p-5">
        <div className="text-xs font-semibold uppercase tracking-wider">Amount</div>
        <div className="mt-2 flex flex-wrap gap-2">{AMOUNTS.map((a) => (
          <button key={a} type="button" onClick={() => { setAmount(a); setOther(''); }} className={`min-h-11 min-w-16 border-2 px-4 text-base font-bold ${!other && amount === a ? 'border-foreground bg-foreground text-background' : 'border-border'}`}>${a}</button>))}
          <label className="flex min-h-11 items-center gap-1 border-2 border-border px-3 focus-within:border-foreground"><span className="font-bold">$</span>
            <input inputMode="decimal" aria-label="Other amount" placeholder="Other" value={other} onChange={(e) => setOther(e.target.value.replace(/[^\d.]/g, ''))} className="w-20 bg-transparent text-base font-bold outline-none" /></label>
        </div>

        <div className="mt-5 text-xs font-semibold uppercase tracking-wider">Pay with</div>
        <div className="mt-2 flex flex-wrap gap-2">{METHODS.map((m) => (
          <button key={m} type="button" onClick={() => setMethod(m)} className={`min-h-11 border-2 px-4 text-sm font-semibold ${method === m ? 'border-foreground bg-foreground text-background' : 'border-border'}`}>{m === 'Credit Card' ? 'Credit card' : m}</button>))}</div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="dn-name" className="text-xs font-semibold uppercase tracking-wider">Your name (optional)</label><input id="dn-name" value={name} onChange={(e) => setName(e.target.value)} disabled={anonymous} className={`${field} disabled:opacity-50`} /></div>
          <div><label htmlFor="dn-email" className="text-xs font-semibold uppercase tracking-wider">Email for a receipt (optional)</label><input id="dn-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} /></div>
        </div>
        <label className="mt-3 flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4" />Keep my gift anonymous</label>
        <div className="mt-2"><label htmlFor="dn-note" className="text-xs font-semibold uppercase tracking-wider">A note to the paper (optional)</label>
          <textarea id="dn-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 block w-full border border-input bg-background p-3 text-sm" /></div>

        {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
        <button type="button" onClick={() => void submit()} disabled={busy} className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 bg-primary px-6 text-base font-bold text-primary-foreground disabled:opacity-50 sm:w-auto">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-5 w-5" />}Donate {total >= 1 ? money(total) : ''}</button>
        <p className="mt-3 text-xs text-muted-foreground">Questions about giving? Call George at 917-652-9128.</p>
      </div>
    </section>
  );
}
